import React from 'react'
import { api } from './api.js'

const AuthContext = React.createContext(null)

const TOKEN_KEY = 'deltareloaded_token'
const LEGACY_KEYS = ['auth_token', 'token', 'jwt', 'access_token']

function loadToken() {
  const t = localStorage.getItem(TOKEN_KEY)
  if (t) return t
  for (const k of LEGACY_KEYS) {
    const v = localStorage.getItem(k)
    if (v) {
      // Migrate legacy token keys into the canonical key
      localStorage.setItem(TOKEN_KEY, v)
      return v
    }
  }
  return ''
}

export function AuthProvider({ children }) {
  const [token, setToken] = React.useState(() => loadToken())
  const [user, setUser] = React.useState(null)
  const [loading, setLoading] = React.useState(Boolean(token))

  React.useEffect(() => {
    let live = true
    async function bootstrap() {
      if (!token) {
        setLoading(false)
        setUser(null)
        return
      }
      try {
        const me = await api('/api/auth/me', { token })
        if (live) setUser(me.user)
      } catch {
        if (live) {
          setUser(null)
          setToken('')
          localStorage.removeItem(TOKEN_KEY)
        }
      } finally {
        if (live) setLoading(false)
      }
    }
    bootstrap()
    return () => { live = false }
  }, [token])

  async function login(email, password) {
    const data = await api('/api/auth/login', { method: 'POST', body: { email, password } })
    localStorage.setItem(TOKEN_KEY, data.token)
    setToken(data.token)
    setUser(data.user)
  }

  async function register(handle, email, password) {
    const data = await api('/api/auth/register', { method: 'POST', body: { handle, email, password } })
    localStorage.setItem(TOKEN_KEY, data.token)
    setToken(data.token)
    setUser(data.user)
  }


  async function refresh() {
    if (!token) {
      setUser(null)
      return null
    }
    const me = await api('/api/auth/me', { token })
    setUser(me.user)
    return me.user
  }

  function logout() {
    setUser(null)
    setToken('')
    localStorage.removeItem(TOKEN_KEY)
  }

  const value = { token, user, loading, login, register, refresh, logout }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
